import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, fromJS } from 'immutable';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import GlobalElementMetricsActions from 'flux/actions/GlobalElementMetricsActions';
import { isGlobalElementManager } from 'main/components/show/cuesheet/cuesheetViewHelper';
import {
  UPCOMING_EVENT_FILTERS,
  PAST_EVENT_FILTERS,
} from 'shared/constants/EventFilters';
import _ from 'underscore';

const debouncedPopulateGlobalElementRowsMetrics = _.debounce(
  GlobalElementMetricsActions.populate,
  1000,
);

/**
 *  Global Element Metrics Store
 */
export default Reflux.createStore({
  listenables: [GlobalElementMetricsActions],

  mixins: [BaseMixin],

  init() {
    this.setState(this._getInitState());

    this.listenTo(CuesheetStore, 'onCuesheetChange');
  },

  _getInitState() {
    return Map({
      activeEvents: OrderedMap(),
      pastEvents: OrderedMap(),
      proofShowItems: OrderedMap(),
    });
  },

  onCuesheetChange(state) {
    if (!state.get('rowMap')) return null;

    let next = this.getState()
      .set('teamId', state.get('teamId'))
      .set('eventId', state.get('eventId'))
      .set('showId', state.get('showId'))
      .set('itemSetListId', state.get('itemSetListId'))
      .set('itemSetId', state.get('rowMap').keySeq().first());

    let rowSelectionChanged = null;

    if (state.get('selectedCells').size > 0) {
      const { rowId } = state.get('selectedCells').last();

      rowSelectionChanged = rowId !== this.getInState('rowId');

      next = next.set('rowId', rowId);
    }

    this.setStateAndTrigger(next);

    if (isGlobalElementManager() && rowSelectionChanged) {
      debouncedPopulateGlobalElementRowsMetrics();
    }
  },

  onPopulate() {
    const state = this.getState();

    if (!state.get('rowId')) return;

    GlobalElementMetricsActions.getEvents(
      state.get('teamId'),
      state.get('showId'),
      state.get('itemSetId'),
      state.get('rowId'),
    );

    //Commenting out because this is a poorly performing operation on the API side - https://github.com/SHOFLO/Issues/issues/4222
    // GlobalElementMetricsActions.getProofsOfPerformance(
    //   state.get('teamId'),
    //   state.get('showId'),
    //   state.get('itemSetId'),
    //   state.get('rowId')
    // );
  },

  onGetEvents() {
    this.setStateAndTrigger(this.getState().merge(this._getInitState()));
  },

  onGetEventsCompleted(showItems) {
    let next = this.getState();

    let eventItems = this._groupShowsByEvent(showItems, next);

    next = this._populateActiveEvents(eventItems, next);
    next = this._populatePastEvents(eventItems, next);

    this.setStateAndTrigger(next);
  },

  _groupShowsByEvent(showItems) {
    let showLookup = {};

    return OrderedMap().withMutations((next) => {
      showItems.forEach((item) => {
        const event = item.event;
        const show = item.show;

        if (show.deleted || show.archived) return;

        //Prevent more than one show of the same show
        //from being added to the event
        if (showLookup[show._id]) return;
        showLookup[show._id] = true;

        if (next.get(event._id)) {
          return next.setIn([event._id, 'items', item._id], fromJS(item));
        }

        next.set(
          event._id,
          Map({
            event: fromJS(event),
            items: OrderedMap({ [item._id]: fromJS(item) }),
          }),
        );
      });

      return next;
    });
  },

  _populateActiveEvents(eventItems, state) {
    const endOnFilter = UPCOMING_EVENT_FILTERS.get('endOn').$gt;

    const activeEvents = eventItems.filter((item) => {
      const eventEndOn = item.getIn(['event', 'endOn']);
      return eventEndOn > endOnFilter;
    });

    return state.set('activeEvents', activeEvents);
  },

  _populatePastEvents(eventItems, state) {
    const endOnFilter = PAST_EVENT_FILTERS.get('endOn').$lte;

    const pastEvents = eventItems.filter((item) => {
      const eventEndOn = item.getIn(['event', 'endOn']);
      return eventEndOn <= endOnFilter;
    });

    return state.set('pastEvents', pastEvents);
  },

  onGetProofsOfPerformanceCompleted(proofs) {
    const proofMap = OrderedMap().withMutations((next) => {
      proofs.forEach((proof) => {
        const show = proof.show;

        if (show.archived || show.deleted) return;

        next.set(proof._id, fromJS(proof));
      });

      return next;
    });

    this.setInStateAndTrigger('proofShowItems', proofMap);
  },
});
